import React, { useRef, useEffect, useState } from 'react';

const PUPIL_MAX = 7;

function getPupil(el, mx, my) {
  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top  + rect.height / 2;
  const dx = mx - cx, dy = my - cy;
  const angle  = Math.atan2(dy, dx);
  const travel = Math.min(PUPIL_MAX, Math.hypot(dx, dy) / 7);
  return {
    x:    Math.cos(angle) * travel,
    y:    Math.sin(angle) * travel,
    dist: Math.hypot(dx, dy),
  };
}

function Eye({ px, py, sweet, blink, shut, refEl }) {
  const cls = [
    'eyes__eye',
    sweet ? 'eyes__eye--sweet' : '',
    blink ? 'eyes__eye--blink' : '',
    shut  ? 'eyes__eye--shut'  : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} ref={refEl}>
      {/* Eyelid — slides from top, clipped by circular overflow */}
      <div className="eyes__lid" />

      {/* Moving eyeball group — iris + pupil + shine */}
      <div className="eyes__ball" style={{ transform: `translate(${px}px, ${py}px)` }}>
        <div className="eyes__iris" />
        <div className="eyes__pupil" />
        <div className="eyes__shine eyes__shine--1" />
        <div className="eyes__shine eyes__shine--2" />
      </div>

      {/* Heart floats above, outside the moving group */}
      <span className="eyes__heart">♥</span>
    </div>
  );
}

export default function Eyes() {
  const lRef = useRef(null);
  const rRef = useRef(null);
  const [s, setS] = useState({ lx: 0, ly: 0, rx: 0, ry: 0, sweet: false, blink: false, shut: false });

  useEffect(() => {
    const onMove = (e) => {
      if (!lRef.current || !rRef.current) return;
      const l = getPupil(lRef.current, e.clientX, e.clientY);
      const r = getPupil(rRef.current, e.clientX, e.clientY);
      setS(p => ({
        ...p,
        lx: l.x, ly: l.y,
        rx: r.x, ry: r.y,
        sweet: l.dist < 90 || r.dist < 90,
      }));
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    let t;
    const scheduleBlink = () => {
      t = setTimeout(() => {
        setS(p => ({ ...p, blink: true }));
        setTimeout(() => { setS(p => ({ ...p, blink: false })); scheduleBlink(); }, 145);
      }, 2000 + Math.random() * 3500);
    };
    scheduleBlink();
    return () => clearTimeout(t);
  }, []);

  const handleClick = () => {
    setS(p => ({ ...p, shut: true }));
    setTimeout(() => setS(p => ({ ...p, shut: false })), 700);
  };

  return (
    <div className="eyes" aria-hidden="true" onClick={handleClick}>
      <div className="eyes__wrap">
        <Eye px={s.lx} py={s.ly} sweet={s.sweet} blink={s.blink} shut={s.shut} refEl={lRef} />
        <div className={`eyes__blush${s.sweet ? ' eyes__blush--on' : ''}`} />
      </div>
      <div className="eyes__wrap">
        <Eye px={s.rx} py={s.ry} sweet={s.sweet} blink={s.blink} shut={s.shut} refEl={rRef} />
        <div className={`eyes__blush${s.sweet ? ' eyes__blush--on' : ''}`} />
      </div>
    </div>
  );
}
